/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import { EmptyState, PageSkeleton, SectionLabel, StatusChip } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY, riseAt } from '../../components/common/ui-kit';
import FormSelect from '../../components/forms/form-select';

import { evalSuitesService } from '../../services/eval-suites.service';
import { evalCasesService } from '../../services/eval-cases.service';
import { promptsService } from '../../services/prompts.service';
import { ragsService } from '../../services/rags.service';
import { agentsService } from '../../services/agents.service';
import { functionsService } from '../../services/functions.service';
import { aiModelsService } from '../../services/ai-models.service';
import { projectsService } from '../../services/projects.service';
import { valueSetsService } from '../../services/value-sets.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { EvalCase } from '../../types';

const TYPE_OPTIONS = [
  { label: 'Agent', value: 'agent' },
  { label: 'Prompt', value: 'prompt' },
  { label: 'RAG', value: 'rag' },
  { label: 'Model', value: 'model' },
];

// What a suite's cases run against. These are the families with a registered
// target adapter, which is also what the API validates against — so nothing here
// can be saved and then error at run time for want of an adapter.
const TARGET_TYPE_OPTIONS = [
  { label: 'Prompt', value: 'prompt' },
  { label: 'Model (raw)', value: 'model' },
  { label: 'Agent', value: 'agent' },
  { label: 'RAG', value: 'rag' },
  { label: 'Function', value: 'function' },
];
// Every target family now has a list endpoint behind it. The agent picker stays
// EDITABLE rather than a closed list: agents registered in code (rather than in
// Agent Management) have no row to offer, and a suite pointing at one must still
// be authorable.
const FREE_TEXT_TARGETS = new Set<string>();

// Every grading method and metric key the API accepts — a hand-written mirror of
// GRADER_TYPES / METRIC_KEYS in eval_suite_model.py, checked against them by
// tests/ai/test_frontend_mirrors_backend.py. The value sets supply LABELS; these
// fill in anything the set does not carry yet, because the API validates against
// the code lists and an accepted option missing from the dropdown is unreachable.
const GRADER_TYPE_OPTIONS = [
  { label: 'Exact match', value: 'exact' },
  { label: 'Contains', value: 'contains' },
  { label: 'JSON match', value: 'json_match' },
  { label: 'Source match', value: 'source_match' },
  { label: 'Context precision', value: 'context_precision' },
  { label: 'LLM judge', value: 'llm_judge' },
  { label: 'Regex', value: 'regex' },
  { label: 'Length', value: 'length' },
];

const METRIC_KEY_OPTIONS = [
  { label: 'Overall', value: 'OVERALL' },
  { label: 'Tool calls', value: 'TOOL_CALLS' },
  { label: 'Task completion', value: 'TASK_COMPLETION' },
  { label: 'Argument correctness', value: 'ARGUMENT_CORRECTNESS' },
  { label: 'Answer correctness', value: 'ANSWER_CORRECTNESS' },
  { label: 'Answer relevancy', value: 'ANSWER_RELEVANCY' },
  { label: 'Groundedness', value: 'GROUNDEDNESS' },
  { label: 'Factual consistency', value: 'FACTUAL_CONSISTENCY' },
  { label: 'Retrieval relevance', value: 'RETRIEVAL_RELEVANCE' },
  { label: 'Citation correctness', value: 'CITATION_CORRECTNESS' },
  { label: 'Contextual recall', value: 'CONTEXTUAL_RECALL' },
  { label: 'Context precision', value: 'CONTEXT_PRECISION' },
  { label: 'Answer quality', value: 'ANSWER_QUALITY' },
  { label: 'Exact match', value: 'EXACT_MATCH' },
  { label: 'Contains', value: 'CONTAINS' },
  { label: 'JSON match', value: 'JSON_MATCH' },
  { label: 'Non-toxicity', value: 'NON_TOXICITY' },
  { label: 'PII protection', value: 'PII_PROTECTION' },
  { label: 'Refusal correctness', value: 'REFUSAL_CORRECTNESS' },
  { label: 'Regex match', value: 'REGEX_MATCH' },
  { label: 'Length limit', value: 'LENGTH_LIMIT' },
];
// Every key the API accepts without the custom-metric rules: the built-ins plus
// whatever the evalMetric value set lists, which an admin can extend. The form
// adds the set's codes once it loads (see the effect in EvalForm).
const knownMetricKeys = new Set([
  ...METRIC_KEY_OPTIONS.map((o) => o.value),
  'HALLUCINATION',   // pre-rename key; the API reads it as FACTUAL_CONSISTENCY
]);

/** Value-set entries first (their labels are admin-editable), then any built-in
 *  option the set does not list yet. */
const withBuiltIns = (items: { code: string; label: string }[] | undefined,
                      builtIns: { label: string; value: string }[]) => {
  const fromSet = (items || []).map((i) => ({ value: i.code, label: i.label }));
  const seen = new Set(fromSet.map((o) => o.value));
  return [...fromSet, ...builtIns.filter((o) => !seen.has(o.value))];
};

/** A model's declared parameters, as the API stores them (ai_model_master.paramsSchema). */
type ParamRule = { default?: unknown; min?: number; max?: number; enum?: unknown[] };

const graderSchema = z
  .object({
    type: z.string().optional().or(z.literal('')),
    model: z.string().optional().or(z.literal('')),
    passThreshold: z.number().min(0).max(1).nullable().optional(),
    instructions: z.string().optional().or(z.literal('')),
    caseSensitive: z.boolean().optional(),
  })
  .optional();

const metricSchema = z.object({
  key: z.string().min(1, 'Metric name is required'),
  graderType: z.string().min(1, 'Method is required'),
  model: z.string().optional().or(z.literal('')),
  instructions: z.string().optional().or(z.literal('')),
  caseSensitive: z.boolean().optional(),
  weight: z.number().min(0).nullable().optional(),
  passThreshold: z.number().min(0).max(1).nullable().optional(),
  pattern: z.string().optional().or(z.literal('')),
  minLength: z.number().int().min(0).nullable().optional(),
  maxLength: z.number().int().min(0).nullable().optional(),
  referenceFree: z.boolean().optional(),
}).superRefine((m, ctx) => {
  // The same rules the API applies — caught here so the author sees them on
  // the field instead of as a 422 after saving.
  const key = (m.key || '').trim().toUpperCase();
  if (key && !knownMetricKeys.has(key)) {
    if (!/^[A-Z][A-Z0-9_]{1,63}$/.test(key)) {
      ctx.addIssue({ code: 'custom', path: ['key'], message: 'Letters, digits and _ only' });
    } else if (m.graderType !== 'llm_judge' || !(m.instructions || '').trim()) {
      ctx.addIssue({ code: 'custom', path: ['key'], message: 'A custom metric needs the LLM judge method and its own instructions' });
    }
  }
  if (m.graderType === 'regex' && !(m.pattern || '').trim()) {
    ctx.addIssue({ code: 'custom', path: ['pattern'], message: 'A regex metric needs a pattern' });
  }
  if (m.graderType === 'length') {
    if (m.minLength == null && m.maxLength == null) {
      ctx.addIssue({ code: 'custom', path: ['maxLength'], message: 'Set a minimum, a maximum or both' });
    } else if (m.minLength != null && m.maxLength != null && m.minLength > m.maxLength) {
      ctx.addIssue({ code: 'custom', path: ['minLength'], message: 'Minimum is above the maximum' });
    }
  }
});

const suiteCaseSchema = z.object({
  caseRef: z.string().min(1),
  modelIdOverride: z.string().optional().or(z.literal('')),
});

const suiteSchema = z.object({
  suiteCode: z.string().min(1, 'Suite code is required').max(150).trim(),
  name: z.string().min(1, 'Name is required').max(200).trim(),
  type: z.string().min(1, 'Type is required'),
  description: z.string().max(1000).optional().or(z.literal('')),
  version: z.number().nullable().optional(),
  grader: graderSchema,
  metrics: z.array(metricSchema).min(1, 'At least one metric is required'),
  passThreshold: z.number().min(0).max(1).nullable().optional(),
  cases: z.array(suiteCaseSchema).optional(),
  // Default target: what the cases run against. 'prompt' composes the prompt
  // around the case input; 'model' sends it raw to a named model.
  targetType: z.string().optional().or(z.literal('')),
  targetId: z.string().optional().or(z.literal('')),
  // Which version of the target to grade. null = whatever is active at run time.
  // Pinning one is what lets a CANDIDATE be evaluated before it is activated,
  // which is the gate activation checks.
  targetVersion: z.number().nullable().optional(),
  // Prompt targets may also be pinned by tag ('prod', 'staging', …).
  targetTag: z.string().nullable().optional(),
  // A model target's call settings: the system prompt, and the model's own
  // parameters (blank = the model's default, so not sent).
  targetSystem: z.string().max(20000).optional().or(z.literal('')),
  targetParams: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()]).nullable()).optional(),
  // Optional speed gate: a run whose p95 answer time exceeds it fails.
  maxLatencyP95Ms: z.number().int().min(1).max(3_600_000).nullable().optional(),
  appId: z.string().trim().optional().or(z.literal('')),
});

type SuiteFormData = z.infer<typeof suiteSchema>;

/** A model target's system prompt and the parameters the author actually set —
 *  a blank one is left to the model's own default rather than sent empty. */
const modelSettings = (data: Pick<SuiteFormData, 'targetSystem' | 'targetParams'>) => {
  const params = Object.fromEntries(
    Object.entries(data.targetParams || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));
  return {
    ...(data.targetSystem?.trim() ? { system: data.targetSystem.trim() } : {}),
    ...(Object.keys(params).length ? { params } : {}),
  };
};

const EvalForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'eval', basePath: '/evals' });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const [caseToAdd, setCaseToAdd] = useState<string | null>(null);
  const [openModel, setOpenModel] = useState<Set<string>>(new Set());  // rows showing the model override
  const toggleModel = (fid: string) =>
    setOpenModel((prev) => { const n = new Set(prev); if (n.has(fid)) n.delete(fid); else n.add(fid); return n; });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<SuiteFormData>({
    resolver: zodResolver(suiteSchema),
    defaultValues: {
      suiteCode: '',
      name: '',
      type: '',
      description: '',
      version: 1,
      grader: { type: '', model: '', passThreshold: 0.7, instructions: '', caseSensitive: false },
      metrics: [{ key: 'ANSWER_QUALITY', graderType: 'llm_judge', model: '', instructions: '', caseSensitive: false, weight: 1, passThreshold: null }],
      passThreshold: 0.7,
      cases: [],
      targetType: 'prompt', targetId: '', targetVersion: null, targetTag: null,
      targetSystem: '', targetParams: {}, maxLatencyP95Ms: null,
      appId: '',
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'cases' });
  const { fields: metricFields, append: appendMetric, remove: removeMetric } = useFieldArray({ control, name: 'metrics' });

  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { data: typeVS } = useQuery({
    queryKey: ['value-sets', 'evalType'],
    queryFn: () => valueSetsService.getByKey('evalType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  const typeOptions = typeVS?.items?.length
    ? typeVS.items.map((i) => ({ value: i.code, label: i.label }))
    : TYPE_OPTIONS;

  const { data: graderVS } = useQuery({
    queryKey: ['value-sets', 'graderType'],
    queryFn: () => valueSetsService.getByKey('graderType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  const graderTypeOptions = withBuiltIns(graderVS?.items, GRADER_TYPE_OPTIONS);

  const { data: metricVS } = useQuery({
    queryKey: ['value-sets', 'evalMetric'],
    queryFn: () => valueSetsService.getByKey('evalMetric', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  const metricKeyOptions = withBuiltIns(metricVS?.items, METRIC_KEY_OPTIONS);
  useEffect(() => {
    (metricVS?.items || []).forEach((i) => knownMetricKeys.add(String(i.code).toUpperCase()));
  }, [metricVS]);
  // Subject/"run against" families — admin-editable via the Value Sets screen,
  // falling back to the built-in adapter list until the set is seeded.
  const { data: targetTypeVS } = useQuery({
    queryKey: ['value-sets', 'evalTargetType'],
    queryFn: () => valueSetsService.getByKey('evalTargetType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  // The value set carries UPPERCASE codes ('RAG'); the form, the backend and every
  // comparison below use the lowercase adapter keys ('rag'). Unnormalised, picking
  // RAG, Model, Agent or Function fell through to the prompt list. The set only
  // supplies LABELS, and only for families that have a runnable adapter —
  // PIPELINE/EXTRACTION/… were offered and then refused with a 422.
  const runnable = new Set(TARGET_TYPE_OPTIONS.map((o) => o.value));
  const targetTypeOptions = targetTypeVS?.items?.length
    ? targetTypeVS.items
        .map((i) => ({ value: String(i.code).toLowerCase(), label: i.label }))
        .filter((o) => runnable.has(o.value))
    : TARGET_TYPE_OPTIONS;
  const suiteType = watch('type');
  const targetType = watch('targetType') || 'prompt';
  const targetId = watch('targetId') || '';
  // A model suite measures the model alone, so it can only run against one — the
  // API refuses anything else. Lock the picker rather than let a save fail.
  const modelSuite = suiteType === 'model';
  useEffect(() => {
    if (modelSuite && targetType !== 'model') {
      setValue('targetType', 'model', { shouldDirty: true });
      setValue('targetId', '', { shouldDirty: true });
      setValue('targetVersion', null, { shouldDirty: true }); setValue('targetTag', null, { shouldDirty: true });
    }
  }, [modelSuite, targetType, setValue]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const { data: suiteData, isLoading: fetchLoading } = useQuery({
    queryKey: ['eval-suites', id],
    queryFn: () => evalSuitesService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Case library for the picker, scoped to the selected project. Filtered to the
  // suite's type (a suite only holds cases of its own type).
  const { data: casesResp, isFetching: casesLoading } = useQuery({
    queryKey: ['eval-cases', 'options', selectedAppId || 'all', suiteType || 'all'],
    queryFn: () => evalCasesService.list({
      page: 1, page_size: 500, app_id: selectedAppId || undefined,
      type: suiteType || undefined,
    }),
    staleTime: 5 * 60 * 1000,
  });
  const caseById = useMemo(() => {
    const map: Record<string, EvalCase> = {};
    (casesResp?.data || []).forEach((c) => { map[c._id] = c; });
    return map;
  }, [casesResp]);

  // Prompts for the "run against" target picker.
  const { data: promptsResp, isFetching: promptsLoading } = useQuery({
    queryKey: ['prompts', 'options'],
    queryFn: () => promptsService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const promptOptions = (promptsResp?.data || []).map((p) => ({ value: p._id, label: p.name }));

  // RAGs for the "run against" picker, when the target is a rag.
  const { data: ragsResp, isFetching: ragsLoading } = useQuery({
    queryKey: ['rags', 'options', selectedAppId || 'all'],
    queryFn: () => ragsService.list({ page: 1, page_size: 100, app_id: selectedAppId || undefined }),
    staleTime: 5 * 60 * 1000,
  });
  const ragOptions = (ragsResp?.data || []).map((r) => ({
    value: r._id, label: r.name ? `${r.ragCode} · ${r.name}` : r.ragCode,
  }));

  // Agents from Agent Management. A suite stores the agentCode, not the row id,
  // because a code-registered agent has no row — the key is the shared namespace.
  const { data: agentsResp, isFetching: agentsLoading } = useQuery({
    queryKey: ['agents', 'options', selectedAppId || 'all'],
    queryFn: () => agentsService.list({ page: 1, page_size: 100, status: 'ACTIVE', app_id: selectedAppId || undefined }),
    staleTime: 5 * 60 * 1000,
  });
  const agentOptions = (agentsResp?.data || []).map((a) => ({
    value: a.agentCode, label: a.name ? `${a.agentCode} · ${a.name}` : a.agentCode,
  }));

  // Functions declared as eval targets (function_master, capability=EVAL_TARGET).
  const { data: functionsResp, isFetching: functionsLoading } = useQuery({
    queryKey: ['functions', 'eval-targets', selectedAppId || 'all'],
    queryFn: () => functionsService.list({ page: 1, page_size: 100, capability: 'EVAL_TARGET', app_id: selectedAppId || undefined }),
    staleTime: 5 * 60 * 1000,
  });
  const functionOptions = (functionsResp?.data || []).map((f: any) => ({
    value: f._id, label: f.functionCode + (f.description ? ` — ${f.description}` : ''),
  }));

  // Versions of the RAG under test. Offered only for a rag target, because it
  // is the only target family whose versions are addressable.
  const { data: ragVersions = [] } = useQuery({
    queryKey: ['rag-versions', targetId],
    queryFn: () => ragsService.listVersions(targetId),
    enabled: targetType === 'rag' && !!targetId,
    staleTime: 60 * 1000,
    retry: false,
  });
  // Versions and tags of the prompt under test.
  const { data: promptVersions = [] } = useQuery({
    queryKey: ['prompt-versions', targetId],
    queryFn: () => promptsService.listVersions(targetId),
    enabled: targetType === 'prompt' && !!targetId,
    staleTime: 60 * 1000,
    retry: false,
  });
  const promptPinOptions = [
    { value: '', label: 'Whichever version is active' },
    ...[...new Set(promptVersions.flatMap((v) => v.tags || []))].sort()
      .map((t) => ({ value: `tag:${t}`, label: `Tag “${t}” (follows the tag)` })),
    ...promptVersions.filter((v) => v.status !== 'DISCARDED')
      .map((v) => ({ value: String(v.version), label: `v${v.version} · ${v.status}` })),
  ];
  const ragVersionOptions = [
    { value: '', label: 'Whichever version is active' },
    ...[...ragVersions]
      .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
      .map((v) => ({ value: String(v.version), label: `v${v.version} · ${v.status}` })),
  ];

  const { data: aiModelsResponse, isFetching: modelsLoading } = useQuery({
    // runnable_only: every picker here (model target, judge, per-case
    // override) sends the model a prompt.
    queryKey: ['ai-models', 'options', 'runnable', selectedAppId || 'all'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100, app_id: selectedAppId || undefined, runnable_only: true }),
    staleTime: 5 * 60 * 1000,
  });
  // value = _id (unique): several rows can share a modelId, and resolving a
  // judge/override model by name then fails with AI_MODEL_AMBIGUOUS.
  const modelOptions = (aiModelsResponse?.data || []).map((m) => ({ label: m.displayName, value: m._id }));
  // The parameters the model under test accepts — the API rejects any other, so
  // the form offers exactly these, with the model's own bounds and default.
  const targetModel = targetType === 'model'
    ? (aiModelsResponse?.data || []).find((m) => m._id === targetId) : undefined;
  const paramEntries = Object.entries((targetModel?.paramsSchema || {}) as Record<string, ParamRule>)
    .filter(([, rule]) => rule && typeof rule === 'object');

  useBreadcrumbTitle(isEditMode ? suiteData?.name : null);

  useEffect(() => {
    if (suiteData && isEditMode) {
      reset({
        suiteCode: suiteData.suiteCode || '',
        name: suiteData.name || '',
        type: suiteData.type || '',
        description: suiteData.description || '',
        version: suiteData.version ?? 1,
        grader: {
          type: (suiteData as any).grader?.type || '',
          model: (suiteData as any).grader?.model || '',
          passThreshold: (suiteData as any).grader?.passThreshold ?? 0.7,
          instructions: (suiteData as any).grader?.instructions || '',
          caseSensitive: !!(suiteData as any).grader?.caseSensitive,
        },
        metrics: ((suiteData as any).metrics?.length
          ? (suiteData as any).metrics
          : [{
              key: 'OVERALL', graderType: (suiteData as any).grader?.type || 'llm_judge',
              model: (suiteData as any).grader?.model || '', instructions: (suiteData as any).grader?.instructions || '',
              caseSensitive: !!(suiteData as any).grader?.caseSensitive, weight: 1,
              passThreshold: (suiteData as any).grader?.passThreshold ?? 0.7,
            }]
        ).map((m: any) => ({
          key: m.key || '', graderType: m.graderType || 'llm_judge', model: m.model || '',
          instructions: m.instructions || '', caseSensitive: !!m.caseSensitive,
          weight: m.weight ?? 1, passThreshold: m.passThreshold ?? null,
          pattern: m.pattern || '', minLength: m.minLength ?? null, maxLength: m.maxLength ?? null,
          referenceFree: !!m.referenceFree,
        })),
        passThreshold: (suiteData as any).passThreshold ?? (suiteData as any).grader?.passThreshold ?? 0.7,
        cases: [...(suiteData.cases || [])]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((c) => ({ caseRef: String(c.caseRef), modelIdOverride: c.modelIdOverride || '' })),
        // A model suite with no target still runs against a model (the run's),
        // and the picker is locked to it — loading it as 'prompt' made the lock
        // rewrite the field and flag an untouched form as changed.
        targetType: suiteData.targetRef?.type || (suiteData.type === 'model' ? 'model' : 'prompt'),
        targetId: suiteData.targetRef?.id || '',
        targetVersion: suiteData.targetRef?.version ?? null,
        targetTag: suiteData.targetRef?.tag ?? null,
        targetSystem: suiteData.targetRef?.system || '',
        targetParams: suiteData.targetRef?.params || {},
        maxLatencyP95Ms: suiteData.maxLatencyP95Ms ?? null,
        appId: (suiteData as any).appId || '',
      });
    }
  }, [suiteData, isEditMode, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEditMode && id ? evalSuitesService.update(id, data) : evalSuitesService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-suites'] });
      toast.current?.show({
        severity: 'success', summary: 'Success',
        detail: `Eval suite ${isEditMode ? 'updated' : 'created'} successfully`, life: 3000,
      });
      setTimeout(() => navigateToList(), 1000);
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} eval suite`), life: 10000,
      });
    },
  });

  const isSaving = mutation.isPending;

  const onSubmit = (data: SuiteFormData) => {
    const payload: any = {
      suiteCode: data.suiteCode,
      name: data.name,
      type: data.type,
      description: data.description || null,
      version: data.version ?? 1,
      cases: (data.cases || []).map((c, index) => ({
        caseRef: c.caseRef,
        order: index,
        modelIdOverride: c.modelIdOverride || null,
      })),
    };

    // Default target the suite runs against: a prompt, or cleared.
    payload.targetRef = data.targetId
      ? {
          type: data.targetType || 'prompt',
          id: data.targetId,
          // RAG and prompt targets are versioned; sending neither means "the active one".
          ...((data.targetType === 'rag' || data.targetType === 'prompt') && data.targetVersion != null
            ? { version: data.targetVersion }
            : {}),
          ...(data.targetType === 'prompt' && data.targetVersion == null && data.targetTag
            ? { tag: data.targetTag }
            : {}),
          ...(data.targetType === 'model' ? modelSettings(data) : {}),
        }
      : null;
    // null clears a budget the suite had.
    payload.maxLatencyP95Ms = data.maxLatencyP95Ms ?? null;

    // Named scoring metrics + the overall pass gate. modelId is source of truth.
    payload.metrics = (data.metrics || []).map((m) => ({
      key: m.key,
      graderType: m.graderType,
      model: m.graderType === 'llm_judge' ? (m.model || null) : null,
      instructions: m.graderType === 'llm_judge' ? (m.instructions || null) : null,
      caseSensitive: ['exact', 'contains', 'regex'].includes(m.graderType) ? !!m.caseSensitive : false,
      weight: m.weight ?? 1,
      // Blank = no bar of its own; the suite's bar gates the case.
      passThreshold: m.passThreshold ?? null,
      // Each method's own rule, and only its own — the API drops strays anyway.
      ...(m.graderType === 'regex' ? { pattern: m.pattern } : {}),
      ...(m.graderType === 'length' ? { minLength: m.minLength ?? null, maxLength: m.maxLength ?? null } : {}),
      ...(m.graderType === 'llm_judge' && !knownMetricKeys.has(m.key.trim().toUpperCase()) && m.referenceFree
        ? { referenceFree: true } : {}),
    }));
    payload.passThreshold = data.passThreshold ?? 0.7;

    if (!isEditMode && data.appId) payload.appId = data.appId;
    mutation.mutate(payload);
  };

  // Without this, a form that fails validation does nothing at all on submit:
  // handleSubmit swallows the error, no request is sent, and the invalid field
  // may be one the author has scrolled past. Say what is wrong and go there.
  const onInvalid = (formErrors: FieldErrors<SuiteFormData>) => {
    const first = Object.keys(formErrors)[0];
    if (!first) return;
    const err: any = (formErrors as any)[first];
    // A field array reports per-row errors; the message lives on the first bad row.
    const message: string =
      err?.message ||
      (Array.isArray(err) && err.find(Boolean)
        ? Object.values(err.find(Boolean) as object).find((v: any) => v?.message)?.message
        : '') ||
      'A required field is missing.';
    toast.current?.show({
      severity: 'warn',
      summary: 'Check the highlighted field',
      detail: message,
      life: 6000,
    });
    document.querySelector(`[name="${first}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const selectedRefs = new Set((watch('cases') || []).map((c) => c.caseRef));
  const availableCaseOptions = (casesResp?.data || [])
    .filter((c) => !selectedRefs.has(c._id))
    .map((c) => ({ value: c._id, label: c.caseCode ? `${c.name} (${c.caseCode})` : c.name }));

  if (isEditMode && fetchLoading) return <PageSkeleton panels={3} />;

  return (
    <div className="p-4" data-testid="SCR-EvalSuite-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-check-circle"
        onBack={() => handleNavigate('/evals')}
        title={isEditMode ? 'Edit Eval Suite' : 'Create Eval Suite'}
        right={isEditMode && suiteData?.suiteCode && <span className="font-mono text-xs text-ink-muted">{suiteData.suiteCode}</span>}
      />

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-4 w-full">
        {/* Basic */}
        <div className={`${PANEL} ui-rise p-4`} style={riseAt(0)}>
          <SectionLabel icon="pi pi-info-circle" className="mb-3">Suite</SectionLabel>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Controller name="suiteCode" control={control} render={({ field }) => (
            <Input label="Suite Code *" {...field} error={errors.suiteCode?.message} placeholder="suite_support_rag_release" disabled={isEditMode} />
          )} />
          <Controller name="name" control={control} render={({ field }) => (
            <Input label="Name *" {...field} error={errors.name?.message} placeholder="Support RAG — Release" />
          )} />
          <Controller name="type" control={control} render={({ field }) => (
            <FormSelect label="Type *" name={field.name} value={field.value || ''} options={typeOptions} onChange={field.onChange} error={!!errors.type} helperText={errors.type?.message} />
          )} />
          <Controller name="version" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
              <InputNumber value={field.value ?? 1} onValueChange={(e) => field.onChange(e.value ?? 1)} min={1} useGrouping={false} className="w-full" />
            </div>
          )} />
          {!isEditMode && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''} options={projectOptions} onChange={field.onChange} disabled={projectLocked} />
            )} />
          )}
          <Controller name="targetType" control={control} render={({ field }) => (
            <FormSelect
              label="Run against"
              name={field.name}
              value={field.value || 'prompt'}
              options={targetTypeOptions}
              disabled={modelSuite}
              onChange={(e: any) => {
                // The id belongs to the old target family; keep it and the picker
                // would show a prompt selected as a model.
                field.onChange(e.target ? e.target.value : e);
                setValue('targetId', '', { shouldDirty: true });
                setValue('targetVersion', null, { shouldDirty: true }); setValue('targetTag', null, { shouldDirty: true });
                setValue('targetParams', {}, { shouldDirty: true });
              }}
            />
          )} />
          <Controller name="targetId" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {targetType === 'model' ? 'Model' : targetType === 'agent' ? 'Agent key' : targetType === 'rag' ? 'RAG' : 'Prompt'}
              </label>
              {FREE_TEXT_TARGETS.has(targetType) ? (
                <Input
                  label=""
                  value={field.value || ''}
                  onChange={(v: string) => field.onChange(v)}
                  placeholder="registered agent key"
                  data-testid="EvalForm.TargetId"
                />
              ) : (
                <Dropdown
                  value={field.value || null}
                  options={targetType === 'model' ? modelOptions
                    : targetType === 'rag' ? ragOptions
                      : targetType === 'agent' ? agentOptions
                        : targetType === 'function' ? functionOptions : promptOptions}
                  // Editable for agents only: a code-registered key has no row to pick.
                  editable={targetType === 'agent'}
                  onChange={(e: any) => {
                    field.onChange(e.value || '');
                    // A version number only means something for the RAG it came from,
                    // and parameters only for the model whose schema declared them.
                    setValue('targetVersion', null, { shouldDirty: true }); setValue('targetTag', null, { shouldDirty: true });
                    setValue('targetParams', {}, { shouldDirty: true });
                  }}
                  placeholder={targetType === 'agent' ? 'Pick an agent, or type a registered key' : 'Send the case input directly'}
                  // Its list reloads when the project changes; for that second an
                  // empty list said "No available options" — read as "this project
                  // has none" — although it was only still loading.
                  emptyMessage={(targetType === 'model' ? modelsLoading
                    : targetType === 'rag' ? ragsLoading
                      : targetType === 'agent' ? agentsLoading
                        : targetType === 'function' ? functionsLoading : promptsLoading)
                    ? 'Loading…' : 'None in this project'}
                  filter
                  showClear
                  className="w-full"
                  data-testid="EvalForm.TargetId"
                />
              )}
              <p className="text-xs text-ink-muted" style={PRETTY}>
                {targetType === 'model'
                  ? 'Cases run raw against this model — the baseline, with no prompt template around them; only the system prompt and settings below, if you set them.'
                  : targetType === 'agent'
                    ? 'Cases are offered the agent\'s tools (or their own) and graded on which they call. Nothing is executed.'
                    : targetType === 'rag'
                      ? 'Cases are answered by the RAG runtime; the retrieved passages are graded too.'
                      : targetType === 'function'
                        ? 'Cases run through the function\u2019s registered handler; a returned tool-call list is graded like an agent\u2019s.'
                        : "Cases run through this prompt's system role + instructions (the case input is the user turn)."}
                {' '}Clear to send the case input to the run&rsquo;s own model.
              </p>
            </div>
          )} />
          {/* Which version of the RAG the cases are graded against. Without this
              a run always grades whatever is live, so a CANDIDATE could never
              earn the PASS that activating it requires. */}
          {targetType === 'prompt' && !!targetId && (
            <FormSelect
              label="Prompt version"
              name="targetPin"
              value={watch('targetTag') ? `tag:${watch('targetTag')}` : watch('targetVersion') == null ? '' : String(watch('targetVersion'))}
              options={promptPinOptions}
              onChange={(e: any) => {
                const raw = String((e.target ? e.target.value : e) ?? '');
                setValue('targetTag', raw.startsWith('tag:') ? raw.slice(4) : null, { shouldDirty: true });
                setValue('targetVersion', raw && !raw.startsWith('tag:') ? Number(raw) : null, { shouldDirty: true });
              }}
              helperText="Grade a draft or an archived version before it goes live, or follow a tag such as prod."
              data-testid="EvalForm.TargetPromptVersion"
            />
          )}
          {targetType === 'rag' && !!targetId && (
            <Controller name="targetVersion" control={control} render={({ field }) => (
              <FormSelect
                label="RAG version"
                name={field.name}
                value={field.value == null ? '' : String(field.value)}
                options={ragVersionOptions}
                onChange={(e: any) => {
                  const raw = e.target ? e.target.value : e;
                  field.onChange(raw === '' || raw == null ? null : Number(raw));
                }}
                helperText="Pin a CANDIDATE to evaluate it before activation — activation needs a PASS on that exact version. Leave on active for an ongoing regression check."
                data-testid="EvalForm.TargetVersion"
              />
            )} />
          )}
          {/* The call the model is tested under. Same model, different system
              prompt or temperature, is a different test — so it lives on the
              suite, not on whoever remembers to set it before a run. */}
          {targetType === 'model' && !!targetId && (
            <div className="ui-inset flex flex-col gap-3 p-3 sm:col-span-2" data-testid="EvalForm.ModelSettings">
              <Controller name="targetSystem" control={control} render={({ field }) => (
                <TextArea label="System prompt (optional)" {...field} rows={3}
                  placeholder="Sent with every case — e.g. the persona and rules the model runs under in production" />
              )} />
              {paramEntries.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {paramEntries.map(([name, rule]) => (
                    <Controller key={name} name={`targetParams.${name}` as any} control={control} render={({ field }) => {
                      const fallback = rule.default === undefined ? 'model default' : `default ${String(rule.default)}`;
                      if (Array.isArray(rule.enum)) {
                        return (
                          <FormSelect label={name} name={field.name} value={field.value == null ? '' : String(field.value)}
                            options={[{ value: '', label: `Model default${rule.default === undefined ? '' : ` (${String(rule.default)})`}` },
                              ...rule.enum.map((v) => ({ value: String(v), label: String(v) }))]}
                            onChange={(e: any) => {
                              const raw = e.target ? e.target.value : e;
                              const picked = rule.enum!.find((v) => String(v) === raw);
                              field.onChange(raw === '' ? null : picked ?? raw);
                            }} />
                        );
                      }
                      if (typeof rule.default === 'boolean') {
                        return (
                          <FormSelect label={name} name={field.name} value={field.value == null ? '' : String(field.value)}
                            options={[{ value: '', label: `Model default (${rule.default ? 'on' : 'off'})` },
                              { value: 'true', label: 'On' }, { value: 'false', label: 'Off' }]}
                            onChange={(e: any) => {
                              const raw = e.target ? e.target.value : e;
                              field.onChange(raw === '' ? null : raw === 'true');
                            }} />
                        );
                      }
                      if (typeof rule.default === 'number' || rule.min != null || rule.max != null) {
                        return (
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{name}</label>
                            <InputNumber value={typeof field.value === 'number' ? field.value : null}
                              onValueChange={(e) => field.onChange(e.value ?? null)}
                              min={rule.min} max={rule.max} minFractionDigits={0} maxFractionDigits={4}
                              placeholder={fallback} useGrouping={false} className="w-full" />
                          </div>
                        );
                      }
                      return (
                        <Input label={name} value={field.value == null ? '' : String(field.value)}
                          onChange={(v: string) => field.onChange(v === '' ? null : v)} placeholder={fallback} />
                      );
                    }} />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-ink-muted" style={PRETTY}>
                  This model declares no adjustable parameters, so it runs on its own defaults.
                </p>
              )}
            </div>
          )}
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2">
              <TextArea label="Description" {...field} rows={2} placeholder="What this suite checks" />
            </div>
          )} />
          </div>
        </div>

        {/* Scoring metrics */}
        <div className={`${PANEL} ui-rise p-4`} style={riseAt(1)}>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <SectionLabel icon="pi pi-chart-bar">Scoring metrics</SectionLabel>
              <p className="mt-1 max-w-prose text-xs text-ink-muted" style={PRETTY}>
                Each metric scores every case; the case's overall score is their weighted mean.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Controller name="passThreshold" control={control} render={({ field }) => (
                <div className="flex items-center gap-2">
                  <label className="whitespace-nowrap text-xs font-medium text-gray-600 dark:text-gray-300">Overall pass ≥</label>
                  <InputNumber value={field.value ?? 0.7} onValueChange={(e) => field.onChange(e.value ?? 0.7)}
                    min={0} max={1} minFractionDigits={2} maxFractionDigits={2} step={0.05} inputStyle={{ width: '5rem' }} />
                </div>
              )} />
              <Controller name="maxLatencyP95Ms" control={control} render={({ field }) => (
                <div className="flex items-center gap-2" title="A run whose 95th-percentile answer time is above this fails, however well it scored.">
                  <label htmlFor="eval-latency-budget" className="whitespace-nowrap text-xs font-medium text-gray-600 dark:text-gray-300">p95 latency ≤</label>
                  <InputNumber inputId="eval-latency-budget" value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)}
                    min={1} max={3_600_000} useGrouping={false} placeholder="No limit" suffix=" ms" inputStyle={{ width: '7rem' }} />
                </div>
              )} />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {metricFields.map((mf, i) => {
              const gType = watch(`metrics.${i}.graderType`);
              const mKey = (watch(`metrics.${i}.key`) || '').trim().toUpperCase();
              const custom = !!mKey && !metricKeyOptions.some((o) => o.value === mKey);
              return (
                <div key={mf.id} className="ui-inset ui-rise p-3" style={riseAt(Math.min(i, 6))}>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-start">
                    {/* Editable: pick a built-in metric, or type a name for a custom
                        one — which the LLM judge grades by the instructions below. */}
                    <Controller name={`metrics.${i}.key`} control={control} render={({ field }) => (
                      <div className="flex flex-col gap-1">
                        <label htmlFor={`metric-key-${i}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">Metric</label>
                        <Dropdown inputId={`metric-key-${i}`} value={field.value || ''} options={metricKeyOptions}
                          onChange={(e: any) => field.onChange(String(e.value ?? '').toUpperCase())}
                          editable filter placeholder="Pick, or type a custom name" className="w-full"
                          invalid={!!errors.metrics?.[i]?.key} data-testid={`EvalForm.MetricKey.${i}`} />
                        {errors.metrics?.[i]?.key?.message
                          ? <small className="ui-ink ui-ink--bad text-xs">{errors.metrics?.[i]?.key?.message}</small>
                          : custom && <small className="text-xs text-ink-muted">Custom metric — graded by the LLM judge using your instructions.</small>}
                      </div>
                    )} />
                    <Controller name={`metrics.${i}.graderType`} control={control} render={({ field }) => (
                      <FormSelect label="Method" name={field.name} value={field.value || ''} options={graderTypeOptions} onChange={field.onChange} />
                    )} />
                    <Controller name={`metrics.${i}.weight`} control={control} render={({ field }) => (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Weight</label>
                        <InputNumber value={field.value ?? 1} onValueChange={(e) => field.onChange(e.value ?? 1)}
                          min={0} minFractionDigits={0} maxFractionDigits={2} className="w-full" />
                      </div>
                    )} />
                    <Controller name={`metrics.${i}.passThreshold`} control={control} render={({ field }) => (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Own pass ≥ <span className="font-normal text-ink-muted">(optional)</span></label>
                        <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)}
                          placeholder="Suite bar" min={0} max={1} minFractionDigits={2} maxFractionDigits={2} step={0.05} className="w-full" />
                      </div>
                    )} />
                  </div>

                  {gType === 'llm_judge' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <Controller name={`metrics.${i}.model`} control={control} render={({ field }) => (
                        <FormSelect label="Judge model" name={field.name} value={field.value || ''} options={modelOptions} onChange={field.onChange} />
                      )} />
                      <Controller name={`metrics.${i}.instructions`} control={control} render={({ field }) => (
                        <TextArea label="Judge instructions" {...field} rows={2} placeholder="Rubric for this metric" />
                      )} />
                    </div>
                  )}
                  {gType === 'llm_judge' && custom && (
                    <Controller name={`metrics.${i}.referenceFree`} control={control} render={({ field }) => (
                      <div className="mt-3 flex items-center gap-2">
                        <InputSwitch inputId={`metric-ref-free-${i}`} checked={!!field.value} onChange={(e) => field.onChange(e.value)} />
                        <label htmlFor={`metric-ref-free-${i}`} className="text-sm text-gray-700 dark:text-gray-300">
                          Grade from the input alone <span className="text-ink-muted">— cases need no expected result</span>
                        </label>
                      </div>
                    )} />
                  )}
                  {gType === 'regex' && (
                    <Controller name={`metrics.${i}.pattern`} control={control} render={({ field }) => (
                      <div className="mt-3">
                        <Input label="Pattern" value={field.value || ''} onChange={(v: string) => field.onChange(v)}
                          placeholder="e.g. FIR-\d+ — the output must contain a match; anchor with ^…$ for the whole output"
                          error={errors.metrics?.[i]?.pattern?.message} />
                      </div>
                    )} />
                  )}
                  {gType === 'length' && (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(['minLength', 'maxLength'] as const).map((bound) => (
                        <Controller key={bound} name={`metrics.${i}.${bound}`} control={control} render={({ field }) => (
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {bound === 'minLength' ? 'Minimum characters' : 'Maximum characters'}
                            </label>
                            <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)}
                              min={0} useGrouping={false} placeholder="No limit" className="w-full"
                              invalid={!!errors.metrics?.[i]?.[bound]} />
                            {errors.metrics?.[i]?.[bound]?.message && (
                              <small className="ui-ink ui-ink--bad text-xs">{errors.metrics?.[i]?.[bound]?.message}</small>
                            )}
                          </div>
                        )} />
                      ))}
                    </div>
                  )}
                  {(gType === 'exact' || gType === 'contains' || gType === 'regex') && (
                    <Controller name={`metrics.${i}.caseSensitive`} control={control} render={({ field }) => (
                      <div className="flex items-center gap-2 mt-3">
                        <InputSwitch checked={!!field.value} onChange={(e) => field.onChange(e.value)} />
                        <label className="text-sm text-gray-700 dark:text-gray-300">Case-sensitive</label>
                      </div>
                    )} />
                  )}

                  <div className="mt-2 flex justify-end">
                    <span className="ui-press inline-flex">
                      <Button type="button" label="Remove" icon="pi pi-trash" size="small" text severity="danger"
                        onClick={() => removeMetric(i)} disabled={metricFields.length <= 1} />
                    </span>
                  </div>
                </div>
              );
            })}
            <div>
              <span className="ui-press inline-flex">
                <Button type="button" label="Add metric" icon="pi pi-plus" size="small" outlined
                  onClick={() => appendMetric({ key: '', graderType: 'llm_judge', model: '', instructions: '', caseSensitive: false, weight: 1, passThreshold: null,
                    pattern: '', minLength: null, maxLength: null, referenceFree: false })} />
              </span>
            </div>
          </div>
        </div>

        {/* Cases picker */}
        <div className={`${PANEL} ui-rise p-4`} style={riseAt(2)}>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <SectionLabel icon="pi pi-list-check">Cases</SectionLabel>
              <p className="mt-1 max-w-prose text-xs text-ink-muted" style={PRETTY}>
                Reusable cases this suite runs. The same case can live in several suites — edit it once in Eval Cases.
              </p>
            </div>
            <span className="text-xs tabular-nums text-ink-muted">{fields.length}</span>
          </div>
          <div className="flex items-end gap-2 mb-3">
            {/* min-w-0: without it the flex item keeps its content width, and a long
                case label pushes the Add button out of the row on narrow screens. */}
            <div className="min-w-0 flex-1">
              <Dropdown
                value={caseToAdd}
                options={availableCaseOptions}
                onChange={(e: any) => setCaseToAdd(e.value || null)}
                placeholder={suiteType ? 'Pick a case to add' : 'Choose a type first'}
                emptyMessage={casesLoading ? 'Loading…' : 'No cases left to add in this project'}
                filter
                showClear
                disabled={!suiteType}
                className="w-full"
                data-testid="EvalForm.CasePicker"
              />
            </div>
            <Button
              type="button"
              label="Add"
              icon="pi pi-plus"
              size="small"
              className="ui-press"
              disabled={!caseToAdd}
              onClick={() => {
                if (caseToAdd && !selectedRefs.has(caseToAdd)) {
                  append({ caseRef: caseToAdd, modelIdOverride: '' });
                  setCaseToAdd(null);
                }
              }}
            />
          </div>

          {fields.length === 0 ? (
            <EmptyState icon="pi pi-inbox" title="No cases yet"
                        hint="Create reusable cases in Eval Cases, then pick them here. A suite with no cases has nothing to score." />
          ) : (
            <div className="flex flex-col gap-1.5">
              {fields.map((f, index) => {
                const ref = watch(`cases.${index}.caseRef`);
                const c = caseById[String(ref)];
                const overrideVal = watch(`cases.${index}.modelIdOverride`);
                const showModel = openModel.has(f.id) || !!overrideVal;
                return (
                  <div key={f.id} className="ui-inset ui-rise" style={riseAt(Math.min(index, 8))}>
                    <div className="flex items-center gap-3 px-3 py-2">
                      <span className="w-6 text-center text-xs font-semibold tabular-nums text-ink-muted">{index + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-ink truncate">
                            {c ? c.name : <span className="italic text-ink-muted">Unknown case</span>}
                          </span>
                          {(Array.isArray(c?.type) ? c.type : c?.type ? [c.type] : []).map((t: string) => (
                            <StatusChip key={t} tone="neutral" dot={false}>{t}</StatusChip>
                          ))}
                        </div>
                        {c?.caseCode && <div className="truncate font-mono text-xs text-ink-muted">{c.caseCode}</div>}
                      </div>
                      {/* Model override is advanced + optional: tucked behind a toggle. */}
                      <button aria-label="Override the model for just this case"
                        type="button"
                        onClick={() => toggleModel(f.id)}
                        title="Override the model for just this case"
                        className={`ui-press flex h-8 items-center rounded-lg px-2 text-xs transition-colors ${
                          showModel || overrideVal
                            ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300'
                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      >
                        <i className="pi pi-sliders-h" style={{ fontSize: '0.8rem' }} />
                        <span className="ml-1">{overrideVal ? 'model set' : 'model'}</span>
                      </button>
                      <Button type="button" icon="pi pi-trash" severity="danger" text rounded className="ui-press" onClick={() => remove(index)} aria-label="Remove case" />
                    </div>
                    {showModel && (
                      <div className="px-3 pb-2.5 -mt-0.5 ml-9">
                        <Controller name={`cases.${index}.modelIdOverride`} control={control} render={({ field }) => (
                          <Dropdown value={field.value || null} options={modelOptions}
                            onChange={(e: any) => field.onChange(e.value || '')}
                            placeholder="Use the run's model" filter showClear className="w-full sm:w-72" />
                        )} />
                        <p className="mt-1 text-[11px] text-ink-muted">
                          Runs just this case on a different model. Leave empty to use the run's model.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <span className="ui-press inline-flex"><Button type="button" label="Cancel" severity="secondary" outlined onClick={() => handleNavigate('/evals')} /></span>
          <span className="ui-press inline-flex">
            <Button type="submit" label={isSaving ? 'Saving…' : isEditMode ? 'Update Suite' : 'Create Suite'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={isSaving} data-testid="EvalForm.Button.Submit" />
          </span>
        </div>
      </form>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave} />
    </div>
  );
};

export default EvalForm;
